/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  Input,
  OnInit,
  Optional,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NgControl,
  ControlContainer,
} from '@angular/forms';
import {
  FloatLabelType,
  MatFormFieldAppearance,
} from '@angular/material/form-field';
import {map, Observable, startWith} from 'rxjs';

type Item = {value: string | number; label: string};

@Component({
  selector: 'gatewee-select2',
  templateUrl: './gatewee-select2.component.html',
  styleUrls: ['./gatewee-select2.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GateweeSelect2Component implements ControlValueAccessor, OnInit {
  private static nextId = 0;

  @Input() id = `gatewee-select2-${GateweeSelect2Component.nextId++}`;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() items: Item[] = [];
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() loading = false;
  @Input() autocomplete = false;
  @Input() multiple = false;
  @Input() customMessages: Record<string, string> = {};

  @Input() compareWith: (o1: any, o2: any) => boolean = (a, b) => a === b;

  @Input() disabled = false;

  innerValue: any = null;

  selectControl = new FormControl<any>({value: null, disabled: false});
  searchControl = new FormControl<string>('');
  filteredItems!: Observable<Item[]>;

  floatLabel: FloatLabelType = 'auto';

  onChange: (value: any) => void = () => {};
  onTouched = () => {};

  form!: FormGroupDirective['form'];
  control!: FormControl;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() private controlContainer: ControlContainer,
  ) {
    if (this.ngControl) this.ngControl.valueAccessor = this;
  }

  ngOnInit() {
    // autocomplete
    if (this.autocomplete) {
      this.filteredItems = this.searchControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterItems(value ?? '')),
      );
    }

    this.selectControl.valueChanges.subscribe(val => {
      const next = this.multiple
        ? Array.isArray(val)
          ? val
          : []
        : val ?? null;
      this.innerValue = next;
      this.onChange(next);
      this.onTouched();
    });

    if (this.controlContainer && this.ngControl?.name) {
      const parentForm = this.controlContainer as FormGroupDirective;
      this.form = parentForm.form;
      if (this.form) {
        this.control = this.form.get(
          this.ngControl.name as string,
        ) as FormControl;
      }
    }
  }

  private filterItems(value: string): Item[] {
    const filterValue = value?.toLowerCase() || '';
    return this.items.filter(it =>
      it.label.toLowerCase().includes(filterValue),
    );
  }

  writeValue(value: any): void {
    this.innerValue = this.multiple ? value || [] : value ?? null;

    if (!this.autocomplete) {
      this.selectControl.setValue(this.innerValue, {emitEvent: false});
    } else {
      this.searchControl.setValue('', {emitEvent: false});
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.autocomplete) {
      isDisabled
        ? this.searchControl.disable({emitEvent: false})
        : this.searchControl.enable({emitEvent: false});
    } else {
      isDisabled
        ? this.selectControl.disable({emitEvent: false})
        : this.selectControl.enable({emitEvent: false});
    }
  }

  getLabel(value: any): string {
    const item = this.items.find(i => this.compareWith(i.value, value));
    return item ? item.label : '';
  }

  // ===== Autocomplete (múltiplo) =====
  toggleSelection(value: any) {
    if (!this.multiple) return;
    const current: any[] = Array.isArray(this.innerValue)
      ? [...this.innerValue]
      : [];
    const idx = current.findIndex(v => this.compareWith(v, value));
    if (idx >= 0) current.splice(idx, 1);
    else current.push(value);

    this.innerValue = current;
    this.onChange(current);
    this.onTouched();
  }

  isSelected(value: any): boolean {
    return Array.isArray(this.innerValue)
      ? this.innerValue.some((v: any) => this.compareWith(v, value))
      : false;
  }

  removeSelected(value: any) {
    if (!this.multiple) return;
    const current: any[] = Array.isArray(this.innerValue)
      ? [...this.innerValue]
      : [];
    this.innerValue = current.filter(v => !this.compareWith(v, value));
    this.onChange(this.innerValue);
    this.onTouched();
  }
}
